import { useState } from 'react'
import { message } from 'antd'
import {
  listAIVoices,
  generateDialogue,
  generateTtsV2
} from '../../../../../services/api/aiVideoGeneration'
import { useTaskPoller } from '../../../hooks/useTaskPoller'
import { applySceneAudioVersion } from '../../../utils/audioHistory'
import { mergeVoicesById, normalizeVoiceList } from '../../Voiceover/voiceUtils'

export const useVoiceOver = ({
  scenes,
  updateSceneData,
  updateScene,
  videoId
}) => {
  const [voices, setVoices] = useState([])
  const [loadingVoices, setLoadingVoices] = useState(false)

  // Poller for voice listing tasks
  const voiceListPoller = useTaskPoller({ pollInterval: 3000 })

  // Poller for audio generation tasks (voiceover & dialogue)
  const {
    resolveTaskData: resolveAudioTaskData,
    isPolling: isAudioPolling,
    progress: audioProgress,
    status: audioStatus,
    message: audioMessage,
    stopPolling: stopAudioPolling
  } = useTaskPoller({ pollInterval: 3000 })

  /**
   * Validate task output – the task may report status 'success' but the
   * output itself can indicate a failure (e.g. { success: false, error: '...' }).
   */
  const validateOutput = output => {
    if (output?.success === false || output?.error) {
      const errorMsg = output?.error || 'Audio generation failed'
      throw new Error(errorMsg)
    }
    return output
  }

  const fetchVoices = async () => {
    setLoadingVoices(true)
    try {
      const res = await listAIVoices(videoId)
      const result = await voiceListPoller.resolveTaskData(res, {
        getDirectOutput: data =>
          data && data.output !== undefined ? data.output : data,
        isDirectResponse: (data, output) => {
          const status =
            typeof data?.status === 'string' ? data.status.toLowerCase() : ''
          return (
            status === 'success' ||
            data?.success === true ||
            Array.isArray(output?.default_voices) ||
            Array.isArray(output?.uploaded_voices)
          )
        }
      })
      const output = result?.output || result?.audio_url || result?.result || ''
      validateOutput(output)

      // Preserve full metadata (including audio_url) when available.
      const defaultVoices = normalizeVoiceList(
        output?.default_voices || output?.system_voices,
        'default'
      )
      const uploadedVoices = normalizeVoiceList(
        output?.uploaded_voices || output?.user_voices,
        'uploaded'
      )
      const allVoices = mergeVoicesById(defaultVoices, uploadedVoices)

      setVoices(allVoices)
    } catch (err) {
      console.error('Failed to fetch voices', err)
      message.error('Failed to load voices')
    } finally {
      setLoadingVoices(false)
    }
  }

  // Helper function to calculate audio duration from URL
  const calculateAudioDuration = async audioUrl => {
    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl)

      audio.addEventListener('loadedmetadata', () => {
        const duration = audio.duration
        // Round to 2 decimal places
        const roundedDuration = Math.round(duration * 100) / 100
        resolve(roundedDuration)
      })

      audio.addEventListener('error', e => {
        console.error('Error loading audio for duration calculation:', e)
        // Resolve with 0 instead of rejecting to prevent breaking the flow
        resolve(0)
      })

      // Set a timeout to prevent hanging
      setTimeout(() => {
        resolve(0)
      }, 10000) // 10 second timeout
    })
  }

  const generateVoiceForScene = async (sceneId, payload) => {
    // payload should be { voice_id, text, speed_factor?, exaggeration?, ... }

    // Ensure aggregated_voiceover is treated as string. If it's an object, default to ""
    try {
      // Update the scene's voiceover text immediately
      updateSceneData(sceneId, 'aggregated_voiceover', payload.text)

      const res = await generateTtsV2({ ...payload, video_id: videoId })
      const result = await resolveAudioTaskData(res, {
        getDirectOutput: data => {
          if (data && data.output !== undefined) return data.output
          if (data && data.audio_url !== undefined) return data.audio_url
          if (data && data.result !== undefined) return data.result
          return data
        },
        isDirectResponse: (data, output) => {
          const status =
            typeof data?.status === 'string' ? data.status.toLowerCase() : ''
          return status === 'success' || data?.success === true || !!output
        }
      })
      const output = result?.output || result?.audio_url || result?.result || ''
      validateOutput(output)

      const audioUrl =
        (typeof output === 'string' && output) ||
        output?.audio_url ||
        output?.url ||
        ''

      // Calculate audio duration
      const audioDuration = await calculateAudioDuration(audioUrl)

      // Update the scene's audio_link and audio_duration
      updateScene(sceneId, scene =>
        applySceneAudioVersion(
          scene,
          {
            audio_link: audioUrl,
            audio_duration: audioDuration,
            audio_duration_estimated: false
          },
          {
            source: 'scene_regenerate',
            voiceId: payload.voice_id || null,
            audioMode: 'narration',
            textSnapshot: payload.text || scene?.aggregated_voiceover || ''
          }
        )
      )

      message.success('Voiceover generated')
      return audioUrl
    } catch (err) {
      console.error('Voice generation failed', err)
      // Do not write error object to aggregated_voiceover as per new requirement
      message.error('Voiceover generation failed')
      throw err
    }
  }

  const generateDialogueForScene = async (sceneId, payload) => {
    // payload should be { turns: [[voice_id, text], ...], silence_duration?, global_speed_factor? }
    const existing =
      scenes.find(s => s.id === sceneId)?.aggregated_voiceover || {}

    try {
      updateSceneData(sceneId, 'aggregated_voiceover', {
        ...existing,
        isGenerating: true,
        progress: 0,
        url: '',
        error: null,
        turns: payload.turns
      })

      const res = await generateDialogue({ ...payload, video_id: videoId })
      const result = await resolveAudioTaskData(res, {
        getDirectOutput: data => {
          if (data && data.output !== undefined) return data.output
          if (data && data.audio_url !== undefined) return data.audio_url
          if (data && data.result !== undefined) return data.result
          return data
        },
        isDirectResponse: (data, output) => {
          const status =
            typeof data?.status === 'string' ? data.status.toLowerCase() : ''
          return status === 'success' || data?.success === true || !!output
        }
      })
      const output = result?.output || result?.audio_url || result?.result || ''
      validateOutput(output)

      // Extract audio_url from the output object
      const audioUrl = output?.audio_url || output

      // Calculate audio duration
      const audioDuration = await calculateAudioDuration(audioUrl)

      updateSceneData(sceneId, 'aggregated_voiceover', {
        ...existing,
        isGenerating: false,
        progress: 100,
        url: audioUrl,
        error: null,
        turns: payload.turns
      })

      // Update the scene's audio_link and audio_duration
      updateScene(sceneId, scene =>
        applySceneAudioVersion(
          scene,
          {
            audio_link: audioUrl,
            audio_duration: audioDuration,
            audio_duration_estimated: false
          },
          {
            source: 'dialogue_regenerate',
            audioMode: 'dialogue',
            textSnapshot: (payload.turns || [])
              .map(([, text]) => text)
              .filter(Boolean)
              .join(' ')
          }
        )
      )

      message.success('Dialogue generated')
      return audioUrl
    } catch (err) {
      console.error('Dialogue generation failed', err)
      updateSceneData(sceneId, 'aggregated_voiceover', {
        ...existing,
        isGenerating: false,
        progress: 0,
        url: '',
        error: { message: err?.message || 'Generation failed' }
      })
      message.error('Dialogue generation failed')
      throw err
    }
  }

  return {
    voices,
    loadingVoices,
    fetchVoices,
    generateVoiceForScene,
    generateDialogueForScene,
    isAudioPolling,
    audioProgress,
    audioStatus,
    audioMessage,
    stopAudioPolling
  }
}
